
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { ProfilePreferences } from "@/types/profile";

interface OnboardingFormData {
  museumPreference: string;
  planningStyle: string;
  introvertScale: number;
  creativityScale: number;
  connectionPreference: number;
  selectedExperiences: string[];
  memberType: string;
}

export const useOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<OnboardingFormData>({
    museumPreference: "",
    planningStyle: "",
    introvertScale: 5,
    creativityScale: 5,
    connectionPreference: 50,
    selectedExperiences: [],
    memberType: "member",
  });

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No user found');
      }

      // First check if profile preferences already exist
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error checking profile:', profileError);
        throw profileError;
      }

      const preferences: ProfilePreferences = {
        onboardingCompleted: true,
        museumPreference: formData.museumPreference,
        planningStyle: formData.planningStyle,
        introvertScale: formData.introvertScale,
        creativityScale: formData.creativityScale,
        connectionPreference: formData.connectionPreference,
        selectedExperiences: formData.selectedExperiences,
        memberType: formData.memberType
      };

      // Update both preferences and member_type in profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          preferences,
          member_type: formData.memberType  // Update the DB column directly
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Profile created successfully!",
      });

      navigate("/events");
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    loading,
    handleSubmit,
  };
};
